import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

/**
 * WhopSetup - Handles post-purchase account setup
 * 
 * This page is hit after a successful Whop checkout.
 * Two scenarios:
 * 1. User has existing account -> Auto-link subscription and redirect to dashboard
 * 2. New user -> Redirect to registration with email pre-filled
 */
const WhopSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    handleWhopSetup();
  }, []);

  const handleWhopSetup = async () => {
    try {
      const token = searchParams.get('token');
      let whopUserId = searchParams.get('whop_user_id');
      
      // Check if whop_user_id is the literal template string (Whop doesn't support dynamic params)
      if (whopUserId === '{{user.id}}' || whopUserId === '{user.id}' || !whopUserId) {
        console.log('No valid whop_user_id, checking for recent registration session...');
        whopUserId = null;
      }
      
      // Check if token is provided (webhook-based flow)
      if (token) {
        setStatus('Fetching your account details...');
        
        // Fetch registration session
        const { data: session, error: sessionError } = await supabase
          .from('whop_registration_sessions')
          .select('*')
          .eq('token', token)
          .single();
        
        if (sessionError || !session) {
          console.error('Session error:', sessionError);
          setError('Invalid or expired setup link. Please contact support.');
          return;
        }
        
        // Check if session is expired
        if (new Date(session.expires_at) < new Date()) {
          setError('This setup link has expired. Please contact support.');
          return;
        }
        
        // Check if already completed
        if (session.completed) {
          setStatus('Account already set up! Redirecting to login...');
          setTimeout(() => navigate('/authentication?mode=login'), 2000);
          return;
        }
        
        setSessionData(session);
        
        // Check if user is already logged in
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user && user.id === session.auth_user_id) {
          // User is logged in with the correct account
          // Check if profile exists
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            // Mark session as completed
            await supabase
              .from('whop_registration_sessions')
              .update({ completed: true, updated_at: new Date().toISOString() })
              .eq('token', token);
            
            // Redirect based on plan
            setStatus('Setup complete! Redirecting...');
            setTimeout(() => navigate('/app/dashboard'), 1500);
            return;
          }
        }
        
        // User not logged in or different account - redirect to registration
        setStatus('Redirecting to complete your account setup...');
        setTimeout(() => {
          navigate(`/authentication?mode=register&email=${encodeURIComponent(session.email)}&name=${encodeURIComponent(session.full_name || '')}&whop_token=${token}`);
        }, 1500);
        
      } else if (whopUserId) {
        // Direct parameter-based flow (fallback)
        setStatus('Checking your account...');
        
        // First, try to find a registration session by Whop user ID
        const { data: sessionByWhopId } = await supabase
          .from('whop_registration_sessions')
          .select('*')
          .eq('whop_user_id', whopUserId)
          .eq('completed', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (sessionByWhopId && new Date(sessionByWhopId.expires_at) > new Date()) {
          // Found a valid registration session - redirect with token
          setStatus('Setting up your account...');
          setTimeout(() => {
            navigate(`/authentication?mode=register&email=${encodeURIComponent(sessionByWhopId.email)}&name=${encodeURIComponent(sessionByWhopId.full_name || '')}&whop_token=${sessionByWhopId.token}`);
          }, 1500);
          return;
        }
        
        // Check if profile exists with this Whop ID
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('whop_user_id', whopUserId)
          .single();
        
        if (profile) {
          // Profile exists - check if user is logged in
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user && user.id === profile.id) {
            // Already logged in - redirect to dashboard
            setStatus('Welcome back! Redirecting...');
            setTimeout(() => navigate('/app/dashboard'), 1000);
          } else {
            // Not logged in - send to login
            setStatus('Account found! Please log in...');
            setTimeout(() => navigate(`/authentication?mode=login&email=${encodeURIComponent(profile.email)}`), 1500);
          }
        } else {
          // No profile found - webhook may not have completed yet
          setError('Account setup in progress. Please wait a moment and try again, or contact support if this persists.');
        }
      } else {
        // No token and no whop_user_id - check for most recent uncompleted registration
        setStatus('Looking up your purchase...');
        
        console.log('Checking for recent registration sessions...');
        
        // First check if table has any data at all
        const { data: allSessions, error: allError } = await supabase
          .from('whop_registration_sessions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);
        
        console.log('All recent sessions:', allSessions);
        console.log('Query error:', allError);
        
        const { data: recentSession, error: recentError } = await supabase
          .from('whop_registration_sessions')
          .select('*')
          .eq('completed', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        console.log('Recent uncompleted session:', recentSession);
        console.log('Recent error:', recentError);
        
        if (recentSession && new Date(recentSession.expires_at) > new Date()) {
          // Found a recent uncompleted session
          setStatus('Found your account! Setting up...');
          setTimeout(() => {
            navigate(`/authentication?mode=register&email=${encodeURIComponent(recentSession.email)}&name=${encodeURIComponent(recentSession.full_name || '')}&whop_token=${recentSession.token}`);
          }, 1500);
        } else if (allSessions && allSessions.length > 0) {
          // Found sessions but none are valid
          const latestSession = allSessions[0];
          console.log('Latest session details:', {
            completed: latestSession.completed,
            expires_at: latestSession.expires_at,
            is_expired: new Date(latestSession.expires_at) < new Date()
          });
          
          if (latestSession.completed) {
            setError('Your account has already been set up. Please try logging in.');
          } else if (new Date(latestSession.expires_at) < new Date()) {
            setError('Your setup link has expired. Please contact support for a new link.');
          } else {
            setError('Unable to find your purchase. Please check your email for a setup link or contact support.');
          }
        } else {
          setError('No purchase found. The webhook may still be processing. Please wait 30 seconds and refresh, or contact support if this persists.');
        }
      }
      
    } catch (err) {
      console.error('Whop setup error:', err);
      setError(err.message || 'Something went wrong during setup');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Setup Issue
            </h2>
            
            <p className="text-muted-foreground mb-6">
              {error}
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/authentication')}
                fullWidth
              >
                Go to Login
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.location.href = 'mailto:support@earnmyedge.com'}
                fullWidth
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            {status === 'processing' || status.includes('Redirecting') || status.includes('Taking you') ? (
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Icon name="CheckCircle" size={32} color="var(--color-primary)" />
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Setting Up Your Account
          </h2>
          
          <p className="text-muted-foreground mb-6">
            {status}
          </p>

          {sessionData && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 text-left">
              <p className="text-sm text-foreground font-medium mb-2">Account Details</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p><span className="font-medium">Email:</span> {sessionData.email}</p>
                {sessionData.full_name && (
                  <p><span className="font-medium">Name:</span> {sessionData.full_name}</p>
                )}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span>Processing...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhopSetup;
